import type { I18nLocale } from '@ardoq/i18n';
import { LineType } from '@ardoq/api-types';
import { colors } from '@ardoq/design-tokens';
import { SvgSpriteName } from '@ardoq/icons';
import { ARDOQ_DEFAULT_FONT_FAMILY } from '@ardoq/typography';
import {
  ArrowTypeStringValues,
  ComponentAssignmentStrategyStringValues,
  ExteriorNodeLabelModelPositionStringValues,
  HierarchicalLayoutRoutingStyleStringValues,
  InsetsConvertible,
  LayoutOrientationStringValues,
  PartialLayoutOrientationStringValues,
  PartialLayoutRoutingStyleStringValues,
  ShapeNodeShapeStringValues,
  StyleIndicatorZoomPolicyStringValues,
  SubgraphPlacementStringValues,
} from '@ardoq/yfiles';

type FontWeightStringValues =
  // This is the same as `yfiles.Font['weight']`, but improved so TypeScript
  // doesn't just simplify it to `string`
  | 'normal'
  | 'bold'
  | 'light'
  | 'bolder'
  | 'lighter'
  | 'inherit'
  | (string & {});

export type Font = {
  family: string;
  weight: FontWeightStringValues;
  size: number;
};

export type ExteriorLabelLayout = {
  type: 'exterior';
  position: ExteriorNodeLabelModelPositionStringValues;
  insets: InsetsConvertible;
};

export const defaultNodeLabelLayout: ExteriorLabelLayout = {
  type: 'exterior',
  position: 'bottom',
  insets: 2,
};

export type InteriorLabelLayout = {
  type: 'interior';
  position: 'top-left' | 'center';
};

export type EdgeSegmentLabelLayout = {
  type: 'edge-segment';
  position: 'center';
};

export const defaultEdgeLabelLayout: EdgeSegmentLabelLayout = {
  type: 'edge-segment',
  position: 'center',
};

export type LabelLayout =
  | ExteriorLabelLayout
  | InteriorLabelLayout
  | EdgeSegmentLabelLayout;

type IconSize = { width: number; height: number };

export type SvgSpriteLabelIcon = {
  type: 'svg-sprite';
  name: SvgSpriteName;
  color: string;
  size?: IconSize;
};

export type FontAwesomeLabelIcon = {
  type: 'font-awesome';
  fontAwesomeChar: string;
  color: string;
  size?: IconSize;
};

export type ImageLabelIcon = {
  type: 'image';
  imageUrl: string;
  size?: IconSize;
};

export type LabelIcon =
  | SvgSpriteLabelIcon
  | FontAwesomeLabelIcon
  | ImageLabelIcon;

export type LabelStyle = {
  font: Font;
  textFill: string;
  /** Padding inside the label */
  insets: InsetsConvertible;
  icon?: LabelIcon;
};

export const defaultNodeLabelStyle: LabelStyle = {
  font: {
    family: ARDOQ_DEFAULT_FONT_FAMILY,
    weight: '500',
    size: 14,
  },
  textFill: colors.grey950, // textDefault
  insets: 2,
};

export const defaultEdgeLabelStyle: LabelStyle = {
  font: {
    family: ARDOQ_DEFAULT_FONT_FAMILY,
    weight: 'normal',
    size: 12,
  },
  // grey15 is the semantic token textDefault, but since that uses css variables, we cannot use it directly
  textFill: colors.grey950,
  insets: [0, 4],
};

export type Label = {
  text: string;
  style: LabelStyle;
  layout: LabelLayout;
};

export const defaultNodeLabel: Label = {
  text: '',
  style: defaultNodeLabelStyle,
  layout: defaultNodeLabelLayout,
};

export const defaultEdgeLabel: Label = {
  text: '',
  style: defaultEdgeLabelStyle,
  layout: defaultEdgeLabelLayout,
};

/**
 * Common attributes for nodes and edges
 */
export type GraphElement = {
  /**
   * Id of the node/edge. Used for referencing in the graph.
   */
  id: string;
  labels: Label[];
  /**
   * Invoked when this specific node/edge is clicked. Fires alongside the
   * graph-level `onNodeClick` / `onEdgeClick`.
   */
  onClick?: VoidFunction;
};

export type SVGNodeStyle = {
  type: 'svg';
  svg: SVGElement;
};

export type VoidNodeStyle = {
  type: 'void';
};

type ShapeNodeStyleBase = {
  type: 'shape';
  fill: string;
  stroke: Stroke;
};

/** Any shape whose geometry has no further configuration. */
export type PlainShapeNodeStyle = ShapeNodeStyleBase & {
  shape: Exclude<ShapeNodeShapeStringValues, 'round-rectangle'>;
};

export type RoundRectangleNodeStyle = ShapeNodeStyleBase & {
  shape: 'round-rectangle';
  /**
   * Corner radius in world coordinates, clamped to half the node's shorter side.
   * Omitted, the shape is stuck on yFiles' fixed corner size of 10. Set it when
   * the corners have to line up with another shape — a selection indicator drawn
   * at `padding` around this node needs `cornerRadius + padding` to stay
   * concentric.
   */
  cornerRadius?: number;
};

export type ShapeNodeStyle = PlainShapeNodeStyle | RoundRectangleNodeStyle;

/**
 * Wraps another node style and renders a row of dots below the node. Used
 * for per-node badges / indicators that stay attached to the node when it
 * moves, and that are orthogonal to the node's primary visual shape.
 */
export type NodeDot = {
  color: string;
  /**
   * Fires `true` on pointer enter and `false` on leave. The dots sit outside
   * the node's layout bounds, so yFiles' own hit-testing never sees them and
   * this cannot collide with node click or selection.
   */
  onHover?: (isHovered: boolean) => void;
  /**
   * Setting this stops the pointer events on the dot from reaching the canvas,
   * so clicking a dot does not also read as a click on empty canvas.
   */
  onClick?: VoidFunction;
};

export type DotsNodeStyle = {
  type: 'dots';
  wrapped: NodeStyle;
  /** One dot per entry, rendered in a horizontal row below the node. */
  dots: NodeDot[];
};

/** A node's world-coordinate bounds, as passed to canvas render routines. */
export type LayoutBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Escape hatch for a fully custom, fixed-size node drawn directly to an HTML
 * canvas (yFiles `HtmlCanvasVisual`). graph-view stays agnostic about what is
 * drawn — the consumer supplies both the node size and the draw routine. This is
 * strictly opt-in: only nodes that use this style are affected (every other node
 * keeps its SVG style and label-based auto-sizing), so existing consumers are
 * untouched.
 */
export type CanvasNodeStyle = {
  type: 'canvas';
  /** Fixed node size in world units. Canvas nodes are not auto-sized to labels. */
  size: { width: number; height: number };
  /**
   * Draw the node into the 2D context. `layout` is the node's world-coordinate
   * bounds; the context is already transformed to world space, so draw using the
   * supplied coordinates directly. Restore any context state you change.
   */
  render: (ctx: CanvasRenderingContext2D, layout: LayoutBounds) => void;
};

/**
 * The group-container counterpart to {@link CanvasNodeStyle}: the same opt-in
 * `HtmlCanvasVisual` rendering, but for a node that is a group (other nodes declare
 * it as their `parentId`). The layout reserves `insets` inside the group for its
 * children — e.g. a header stripe drawn along the top — and treats `size` as the
 * group's *minimum* size, growing the group to fit its children. Distinct from the
 * leaf style so the inset contract is explicit and required rather than an optional
 * field that's meaningless on a leaf.
 */
export type CanvasGroupNodeStyle = {
  type: 'canvas-group';
  /** Minimum group size in world units; the layout grows the group to fit children. */
  size: { width: number; height: number };
  /**
   * Draw the *expanded* group container into the 2D context. `layout` is the group's
   * live world-coordinate bounds; the context is already in world space, so draw using
   * the supplied coordinates directly. Restore any context state you change. A folded
   * group is drawn by {@link renderCollapsed} instead (see {@link GraphViewProps.collapsibleGroups}).
   */
  render: (ctx: CanvasRenderingContext2D, layout: LayoutBounds) => void;
  /**
   * Draw the group when it is folded (collapsed), instead of {@link render}. Only
   * invoked with {@link GraphViewProps.collapsibleGroups} enabled; `layout` is the
   * folded footprint ({@link collapsedSize}). This is a separate render path — no
   * `collapsed` flag is threaded into {@link render}. If omitted, a folded group falls
   * back to {@link render} at {@link collapsedSize}.
   */
  renderCollapsed?: (
    ctx: CanvasRenderingContext2D,
    layout: LayoutBounds
  ) => void;
  /** Insets the layout reserves inside the group for its children. */
  insets: { top: number; right: number; bottom: number; left: number };
  /**
   * Footprint of the collapsed (folded) group. Used only with
   * {@link GraphViewProps.collapsibleGroups}; defaults to {@link size} if omitted.
   */
  collapsedSize?: { width: number; height: number };
};

export type NodeStyle =
  | SVGNodeStyle
  | VoidNodeStyle
  | ShapeNodeStyle
  | DotsNodeStyle
  | CanvasNodeStyle
  | CanvasGroupNodeStyle;

export type Node = GraphElement & {
  style: NodeStyle;
  parentId?: string;
  selectionStyle?: IndicatorNodeStyle;
};

export type DashStyle = {
  /** Pattern of dash length and gap length */
  pattern: [number, number, ...number[]];
};

export type SolidStroke = {
  line: 'solid';
  color: string;
  thickness: number;
};
export type DashedStroke = {
  line: 'dashed';
  color: string;
  thickness: number;
  dashStyle: DashStyle;
};

export type Stroke = SolidStroke | DashedStroke;

export const defaultSolidStroke: SolidStroke = {
  line: LineType.SOLID,
  color: colors.grey400,
  thickness: 1,
};
export const defaultDashedStroke: DashedStroke = {
  line: LineType.DASHED,
  color: colors.grey400,
  thickness: 1,
  dashStyle: {
    pattern: [3.5, 1.5],
  },
};

export type Arrow = {
  type: ArrowTypeStringValues;
  /** Distance from edge to node */
  cropLength: number;
  color: string;
};

export const defaultArrow: Arrow = {
  type: 'open',
  cropLength: 0,
  color: colors.grey400,
};

export type VoidEdgeStyle = {
  type: 'void';
};

export type PolylineEdgeStyle = {
  type: 'polyline';
  stroke: Stroke;
  sourceArrow: Arrow;
  targetArrow: Arrow;
  /** Radius of  the curve */
  smoothingLength: number;
};

export type EdgeStyle = PolylineEdgeStyle | VoidEdgeStyle;

export const defaultEdgeStyle: PolylineEdgeStyle = {
  type: 'polyline',
  stroke: defaultSolidStroke,
  sourceArrow: {
    ...defaultArrow,
    type: 'none',
  },
  targetArrow: defaultArrow,
  smoothingLength: 5,
};

export type Edge = GraphElement & {
  source: string;
  target: string;
  style: EdgeStyle;
};

export type HierarchicalLayoutDataInstance = {
  nodeHalo: number;
};

export type HierarchicalLayoutData = {
  // We can implement override per node like this:
  // [key: string]: HierarchicalLayoutDataInstance | undefined;
  constant: HierarchicalLayoutDataInstance;
  /**
   * Group edges sharing a source (or target) node into a common bus, reducing
   * the number of distinct edge entry/exit points per node. Opt-in; off by
   * default so other consumers are unaffected.
   */
  groupEdges?: boolean;
};

export const defaultHierarchicalLayoutData: HierarchicalLayoutData = {
  constant: {
    // If an edge strikes vertically through a node label, 25 leaves enough
    // space to see a bit of the edge between the node label and the edge label
    nodeHalo: 25,
  },
};

export const defaultHierarchicalLayoutOptions: HierarchicalLayoutAlgorithmOptions =
  {
    layoutOrientation: 'left-to-right',
    routingStyle: 'orthogonal',
    integratedEdgeLabeling: true,
    componentLayoutEnabled: true,
    considerNodeLabels: true,
  };

export type HierarchicalLayoutAlgorithmOptions = {
  layoutOrientation: LayoutOrientationStringValues;
  routingStyle: HierarchicalLayoutRoutingStyleStringValues;
  integratedEdgeLabeling: boolean;
  componentLayoutEnabled: boolean;
  considerNodeLabels: boolean;
};

export type HierarchicalLayoutAlgorithm = {
  type: 'hierarchical';
  data: HierarchicalLayoutData;
  options: HierarchicalLayoutAlgorithmOptions;
};

export type IncrementalHierarchicalLayoutData = HierarchicalLayoutData & {
  fixedNodes: string[];
};

export type IncrementalHierarchicalLayoutAlgorithm = {
  type: 'incremental-hierarchical';
  data: IncrementalHierarchicalLayoutData;
  options: HierarchicalLayoutAlgorithmOptions;
};

export type PartialLayoutAlgorithmOptions = {
  coreLayout: HierarchicalLayoutAlgorithmOptions;
  layoutOrientation: PartialLayoutOrientationStringValues;
  subgraphPlacement: SubgraphPlacementStringValues;
  componentAssignmentStrategy: ComponentAssignmentStrategyStringValues;
  minimumNodeDistance: number;
  edgeRoutingStyle: PartialLayoutRoutingStyleStringValues;
  allowMovingFixedElements: boolean;
};

export const defaultPartialLayoutOptions: PartialLayoutAlgorithmOptions = {
  coreLayout: defaultHierarchicalLayoutOptions,
  layoutOrientation: 'none',
  subgraphPlacement: 'barycenter',
  componentAssignmentStrategy: 'clustering',
  minimumNodeDistance: 40,
  edgeRoutingStyle: 'orthogonal',
  allowMovingFixedElements: false,
};

export type PartialLayoutAlgorithmData = HierarchicalLayoutData & {
  partialNodes: string[];
};

export type PartialLayoutAlgorithm = {
  type: 'partial';
  data: PartialLayoutAlgorithmData;
  options: PartialLayoutAlgorithmOptions;
};

export type OrganicLayoutAlgorithmOptions = {
  avoidNodeEdgeOverlap: boolean;
  considerNodeLabels: boolean;
  deterministic: boolean;
};

export const defaultOrganicLayoutOptions: OrganicLayoutAlgorithmOptions = {
  avoidNodeEdgeOverlap: true,
  considerNodeLabels: true,
  deterministic: false,
};

export type OrganicLayoutDataInstance = {
  nodeHalo: number;
};

export type OrganicLayoutData = {
  constant: OrganicLayoutDataInstance;
};

export const defaultOrganicLayoutData: OrganicLayoutData = {
  constant: {
    nodeHalo: 25,
  },
};

export type OrganicLayoutAlgorithm = {
  type: 'organic';
  data: OrganicLayoutData;
  options: OrganicLayoutAlgorithmOptions;
};

export type CircularLayoutAlgorithmOptions = {
  /**
   * Edge bundling strength in [0, 1]. 0 disables bundling; higher values pull
   * edges that share a circle partition into tighter bundles, cutting clutter on
   * densely connected graphs.
   */
  edgeBundling: number;
};

export const defaultCircularLayoutOptions: CircularLayoutAlgorithmOptions = {
  edgeBundling: 0,
};

export type CircularLayoutAlgorithm = {
  type: 'circular';
  options: CircularLayoutAlgorithmOptions;
};

export type OrthogonalLayoutAlgorithmOptions = {
  considerNodeLabels: boolean;
};

export const defaultOrthogonalLayoutOptions: OrthogonalLayoutAlgorithmOptions =
  {
    considerNodeLabels: true,
  };

export type OrthogonalLayoutAlgorithm = {
  type: 'orthogonal';
  options: OrthogonalLayoutAlgorithmOptions;
};

export type TreeLayoutAlgorithmOptions = {
  considerNodeLabels: boolean;
};

export const defaultTreeLayoutOptions: TreeLayoutAlgorithmOptions = {
  considerNodeLabels: true,
};

export type TreeLayoutAlgorithm = {
  type: 'tree';
  options: TreeLayoutAlgorithmOptions;
};

export type RadialLayoutAlgorithmOptions = {
  /**
   * Edge bundling strength in [0, 1]. 0 disables bundling; higher values pull
   * edges toward the center, cutting clutter on radial layouts of dense graphs.
   */
  edgeBundling: number;
};

export const defaultRadialLayoutOptions: RadialLayoutAlgorithmOptions = {
  edgeBundling: 0,
};

export type RadialLayoutAlgorithm = {
  type: 'radial';
  options: RadialLayoutAlgorithmOptions;
};

export type CompactDiskLayoutAlgorithmOptions = Record<string, never>;

export const defaultCompactDiskLayoutOptions: CompactDiskLayoutAlgorithmOptions =
  {};

export type CompactDiskLayoutAlgorithm = {
  type: 'compact-disk';
  options: CompactDiskLayoutAlgorithmOptions;
};

export type LayoutAlgorithm =
  | HierarchicalLayoutAlgorithm
  | IncrementalHierarchicalLayoutAlgorithm
  | PartialLayoutAlgorithm
  | OrganicLayoutAlgorithm
  | CircularLayoutAlgorithm
  | OrthogonalLayoutAlgorithm
  | TreeLayoutAlgorithm
  | RadialLayoutAlgorithm
  | CompactDiskLayoutAlgorithm;

export const defaultLayoutAlgorithm: HierarchicalLayoutAlgorithm = {
  type: 'hierarchical',
  data: defaultHierarchicalLayoutData,
  options: defaultHierarchicalLayoutOptions,
};

export type Layout = {
  algorithm: LayoutAlgorithm;
  /**
   * When true, caps the zoom level at 1.0 so the graph never appears zoomed
   * in past 100%. Applies to both full-graph fit and zoomToNodes.
   */
  limitFitContentZoom: boolean;
  /**
   * Padding (in world coordinates) around the content when fitting the
   * viewport. Used as insets for both full-graph fit and zoomToNodes.
   */
  fitGraphBoundsInsets: number;
  /**
   * Animation duration (in ms) for layout transitions when nodes or edges
   * change after the initial render. The initial layout is not animated.
   */
  morphDuration: number;
  /**
   * When set, the initial viewport zooms to fit these specific node IDs
   * instead of the full graph. Useful for focusing on a subset of nodes
   * (e.g. a group node) while keeping the rest of the graph accessible
   * via panning. Falls back to full-graph fit if no matching nodes are found.
   */
  zoomToNodes?: string[];
};

export const defaultLayout: Layout = {
  algorithm: defaultLayoutAlgorithm,
  limitFitContentZoom: false,
  fitGraphBoundsInsets: 40,
  morphDuration: 200,
};

export type FocusIndicator = {
  nodeStyle: NodeStyle;
};

const keyboardFocusStroke: DashedStroke = {
  line: LineType.DASHED,
  color: colors.grey900,
  thickness: 2,
  dashStyle: { pattern: [2, 1.5] },
};

export const keyboardFocusIndicator: IndicatorNodeStyle = {
  wrapped: {
    type: 'shape',
    shape: 'ellipse',
    fill: 'transparent',
    stroke: keyboardFocusStroke,
  },
  padding: 8,
  zoomPolicy: 'world-coordinates',
};

export const keyboardFocusEdgeIndicator: IndicatorEdgeStyle = {
  wrapped: {
    type: 'polyline',
    stroke: keyboardFocusStroke,
    sourceArrow: { type: 'none', color: colors.grey900, cropLength: 8 },
    targetArrow: { type: 'none', color: colors.grey900, cropLength: 8 },
    smoothingLength: 5,
  },
  zoomPolicy: 'world-coordinates',
};

export type GraphItemType = 'node' | 'edge';

export type InputMode = {
  type: 'viewer';
  selectableItems: GraphItemType[];
};

export const defaultInputMode: InputMode = {
  type: 'viewer',
  selectableItems: ['node', 'edge'],
};

export type IndicatorNodeStyle = {
  wrapped: NodeStyle;
  padding: number;
  zoomPolicy: StyleIndicatorZoomPolicyStringValues;
};

export type IndicatorEdgeStyle = {
  wrapped: EdgeStyle;
  zoomPolicy: StyleIndicatorZoomPolicyStringValues;
};

export type SelectionIndicator = {
  nodeStyle: IndicatorNodeStyle;
  edgeStyle: IndicatorEdgeStyle;
};

const transparentBlue60 = `${colors.blue400}FF`;

export const defaultSelectionIndicator: SelectionIndicator = {
  nodeStyle: {
    wrapped: {
      type: 'shape',
      shape: 'ellipse',
      fill: 'transparent',
      stroke: {
        line: LineType.SOLID,
        color: colors.blue400,
        thickness: 2,
      },
    },
    padding: 3,
    zoomPolicy: 'world-coordinates',
  },
  edgeStyle: {
    wrapped: {
      type: 'polyline',
      stroke: {
        line: LineType.SOLID,
        color: transparentBlue60,
        thickness: 4,
      },
      sourceArrow: {
        type: 'none',
        color: colors.blue400,
        cropLength: 8,
      },
      targetArrow: {
        type: 'none',
        color: colors.blue400,
        cropLength: 8,
      },
      smoothingLength: 5,
    },
    zoomPolicy: 'world-coordinates',
  },
};

export type GraphAccessibility = {
  label: string;
  i18nLocale: I18nLocale;
  /** Item types the arrow keys move between. */
  navigableItems: GraphItemType[];
  /** Id of a node or edge to show as focused without the graph having keyboard focus. */
  focusedId?: string;
};

/** Settings for the YFiles GraphComponent */
export type Component = {
  focusIndicator: IndicatorNodeStyle;
  edgeFocusIndicator: IndicatorEdgeStyle;
  inputMode: InputMode;
  selectionIndicator: SelectionIndicator;
};

export const defaultComponent: Component = {
  focusIndicator: keyboardFocusIndicator,
  edgeFocusIndicator: keyboardFocusEdgeIndicator,
  inputMode: defaultInputMode,
  selectionIndicator: defaultSelectionIndicator,
};

export type Selection = {
  nodes: string[];
  edges: string[];
};

export type ContextMenuPosition = {
  x: number;
  y: number;
};

export type MetaballBackgroundGroup = {
  type: 'metaball';
  id: string;
  /**
   * Complete list of nodes that belong to the group. graph-view decides how
   * the individual node blobs are visually bridged so they fuse into a single
   * smooth shape.
   */
  nodeIds: string[];
  color: string;
  opacity?: number;
  padding?: number;
};

export type BackgroundVisualGroup = MetaballBackgroundGroup;

/**
 * Explicit viewport position and zoom level.
 */
export type AbsoluteViewport = {
  type: 'absolute';
  centerX: number;
  centerY: number;
  zoom: number;
};

/**
 * Fit all graph content in the viewport.
 */
export type FitAllContentViewport = {
  type: 'fit-all-content';
};

/**
 * Fit the given node IDs in the viewport. The nodes' coordinates are resolved
 * inside the graph, so callers pass stable IDs rather than positions. Node IDs
 * with no matching node are ignored; an empty (or all-unmatched) set is a no-op.
 */
export type FitNodesViewport = {
  type: 'fit-nodes';
  nodeIds: string[];
};

/**
 * Viewport position and zoom level.
 */
export type Viewport =
  | AbsoluteViewport
  | FitAllContentViewport
  | FitNodesViewport;

export type ViewportProps =
  | {
      /**
       * Controlled viewport. Set to `{ type: 'fit-all-content' }` to fit all
       * content, or `{ type: 'fit-nodes', nodeIds }` to fit specific nodes; the
       * resulting absolute values are reported back via `onViewportChange`. Pass
       * a new object reference to re-apply on demand.
       */
      viewport: Viewport;

      /**
       * Called when the viewport changes — both from user pan/zoom and as the
       * absolute follow-up to `{ type: 'fit-all-content' }` / `{ type: 'fit-nodes' }`.
       * The callback should be referentially stable (e.g. `useState`'s setter or
       * `useCallback`).
       */
      onViewportChange: (viewport: AbsoluteViewport) => void;
    }
  | { viewport?: never; onViewportChange?: never };

export type GraphBackground = 'dotted' | 'none';

export type GraphViewProps = {
  nodes: Node[];
  edges: Edge[];

  layout: Layout;
  component: Component;

  background?: GraphBackground;

  onNodeClick?: (node: Node) => void;
  onEdgeClick?: (edge: Edge) => void;

  onNodeContextMenu?: (node: Node, position: ContextMenuPosition) => void;
  onEdgeContextMenu?: (edge: Edge, position: ContextMenuPosition) => void;

  selection?: Selection;
  onSelectionChange?: (selection: Selection) => void;

  onCanvasClick?: () => void;

  disableMouseScroll?: boolean;

  /**
   * Experimental: make the graph keyboard navigable and announce the focused
   * node or edge to screen readers.
   */
  accessibility?: GraphAccessibility;

  backgroundGroups?: BackgroundVisualGroup[];

  /**
   * Opt-in repaint signal. When this value changes, the graph is invalidated
   * (repainted) without re-running layout. Useful when a node's canvas visual
   * draws content that loads asynchronously (e.g. icon/image sprites) and needs a
   * redraw once it's ready. Consumers that don't draw async content can ignore it.
   */
  invalidateToken?: unknown;

  /**
   * Opt-in: make group nodes collapsible via native yFiles folding. When `true`,
   * the component is backed by a `FoldingManager` view graph, `canvas-group` nodes
   * gain a clickable expander (top-left of the header), and clicking it folds the
   * group to its {@link CanvasGroupNodeStyle.collapsedSize} (children hidden, edges
   * aggregated) / unfolds it again. Default `false` keeps the plain graph and leaves
   * existing consumers completely unchanged. Treated as static for the component's
   * lifetime — toggling it at runtime requires a remount.
   */
  collapsibleGroups?: boolean;

  /**
   * Group node ids to start collapsed (requires {@link collapsibleGroups}). Applied once,
   * after the initial layout: each matching group folds in place to its
   * {@link CanvasGroupNodeStyle.collapsedSize}. Later user collapse/expand is unaffected.
   */
  collapsedGroupIds?: ReadonlySet<string>;

  /**
   * Opt-in "optimize layout" trigger. When this value changes, the current layout is
   * recomputed cleanly from scratch and animated into place — the escape hatch from the
   * incremental (fromSketchMode) relayouts that fold/unfold use, for when the diagram
   * has drifted and the consumer wants a tidy pass. Ignored on the first render.
   */
  relayoutToken?: unknown;

  /**
   * Opt-in zoom triggers. When either value changes, the viewport zooms in / out one step
   * (animated, clamped to the component's min/max zoom) via the native yFiles command.
   * Ignored on the first render.
   */
  zoomInToken?: unknown;
  zoomOutToken?: unknown;

  /**
   * Opt-in bulk-folding triggers (require {@link collapsibleGroups}). When either value
   * changes, every group is collapsed / expanded in one batch, followed by a single
   * relayout. Ignored on the first render.
   */
  collapseAllToken?: unknown;
  expandAllToken?: unknown;

  /**
   * Called whenever the set of collapsed groups changes (bulk fold, initial collapse, or a
   * single expander click), and once after the first layout. Lets the consumer drive
   * collapse-all/expand-all affordances from live fold state. Counts are over the current
   * view graph, so a group hidden inside a collapsed ancestor is not counted.
   */
  onFoldStateChange?: (state: GroupFoldState) => void;
} & ViewportProps;

/** Live folding summary reported by {@link GraphViewProps.onFoldStateChange}. */
export type GroupFoldState = {
  /** Number of group nodes currently in the view graph. */
  groupCount: number;
  /** How many of those groups are collapsed. */
  collapsedCount: number;
};
